import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { 
  FileText, Search, RefreshCw, Check, X, Download, Loader, 
  Sparkles, ChevronDown, ChevronUp, AlertTriangle, Clock,
  Newspaper, Twitter, Instagram, Send, Music, Filter, Zap, Globe,
  Shield, Activity, Layers, Target
} from 'lucide-react';
import {
  ClassificationBanner,
  ExecutiveSummary,
  ReportSection as ProfessionalReportSection,
  ThreatAssessmentPanel,
  RecommendationsPanel,
  SourceReliabilityMatrix,
  ReportFooter,
  IntelligenceReport
} from './IntelligenceReportUI';

export default function ReportGenerator() {
  // Data states
  const [articles, setArticles] = useState([]);
  const [osintResults, setOsintResults] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [searchingOsint, setSearchingOsint] = useState(false);
  
  // Selection states
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [selectedOsint, setSelectedOsint] = useState([]);
  
  // Report generation states
  const [reportTitle, setReportTitle] = useState('');
  const [focusAreas, setFocusAreas] = useState(['military', 'geopolitical']);
  const [classification, setClassification] = useState('UNCLASSIFIED');
  const [reportLanguage, setReportLanguage] = useState('en'); // New: Language state
  const [generating, setGenerating] = useState(false);
  const [report, setReport] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // OSINT search states
  const [osintKeywords, setOsintKeywords] = useState('');
  const [osintPlatforms, setOsintPlatforms] = useState(['Twitter', 'Telegram']);
  
  // UI states
  const [activeTab, setActiveTab] = useState('sources');
  const [expandedSections, setExpandedSections] = useState({});
  const [savedReports, setSavedReports] = useState([]);

  useEffect(() => {
    loadArticles();
    loadSavedReports();
  }, []);

  const loadArticles = async () => {
    setLoadingArticles(true);
    try {
      const response = await axios.get(`${API}/news/feed`);
      setArticles(response.data || []);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoadingArticles(false);
    }
  };

  const loadSavedReports = async () => {
    try {
      const response = await axios.get(`${API}/intelligence/reports`);
      setSavedReports(response.data || []);
    } catch (error) {
      console.error('Error loading reports:', error);
    }
  };

  const searchOsint = async () => {
    if (!osintKeywords.trim()) return;
    setSearchingOsint(true);
    try {
      const response = await axios.post(`${API}/osint/search`, {
        keywords: osintKeywords,
        platforms: osintPlatforms,
      });
      setOsintResults(response.data || []);
    } catch (error) {
      console.error('Error searching OSINT:', error);
    } finally {
      setSearchingOsint(false);
    }
  };

  const toggleArticleSelection = (article) => {
    setSelectedArticles(prev => {
      const exists = prev.find(a => a.id === article.id);
      if (exists) {
        return prev.filter(a => a.id !== article.id);
      }
      return [...prev, article];
    });
  };

  const toggleOsintSelection = (osint) => {
    setSelectedOsint(prev => {
      const exists = prev.find(o => o.id === osint.id);
      if (exists) {
        return prev.filter(o => o.id !== osint.id);
      }
      return [...prev, osint];
    });
  };

  const toggleFocusArea = (area) => {
    setFocusAreas(prev => {
      if (prev.includes(area)) {
        return prev.filter(a => a !== area);
      }
      return [...prev, area];
    });
  };

  const generateReport = async () => {
    if (selectedArticles.length === 0 && selectedOsint.length === 0) {
      alert('Please select at least one source (article or OSINT data)');
      return;
    }
    
    if (!reportTitle.trim()) {
      alert('Please enter a report title');
      return;
    }

    setGenerating(true);
    try {
      const payload = {
        report_title: reportTitle,
        articles: selectedArticles.map(a => ({
          id: a.id,
          title: a.title,
          summary: a.summary,
          source: a.source
        })),
        osint_data: selectedOsint.map(o => ({
          platform: o.platform,
          author: o.author,
          content: o.content,
          url: o.url
        })),
        focus_areas: focusAreas,
        classification: classification,
        language: reportLanguage  // New: Include language preference
      };

      const response = await axios.post(`${API}/intelligence/multi-source`, payload);
      setReport(response.data);
      setActiveTab('report');
      setExpandedSections({
        executive: true,
        findings: true,
        threat: true,
        regional: true,
        strategic: true,
        correlation: true,
        recommendations: true
      });
      loadSavedReports();
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const exportToPptx = async (language = 'en') => {
    if (!report) return;
    setExporting(true);
    try {
      const response = await axios.post(
        `${API}/intelligence/export-multi-pptx/${report.id}`,
        { language: language },
        { responseType: 'blob' }
      );
      
      const langSuffix = language === 'ar' ? '_AR' : '_EN';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DefenseWatch_Intel_Report${langSuffix}_${report.id.slice(0,8)}.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting PPTX:', error);
      alert('Failed to export PowerPoint. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const loadSavedReport = (savedReport) => {
    setReport(savedReport);
    setActiveTab('report');
    setExpandedSections({
      executive: true,
      findings: true,
      threat: true,
      regional: true,
      strategic: true,
      correlation: true,
      recommendations: true
    });
  };

  const clearSelections = () => {
    setSelectedArticles([]);
    setSelectedOsint([]);
    setReportTitle('');
    setReport(null);
  };

  const getPlatformIcon = (platform) => {
    switch (platform?.toLowerCase()) {
      case 'twitter': return <Twitter className="w-4 h-4" />;
      case 'instagram': return <Instagram className="w-4 h-4" />;
      case 'telegram': return <Send className="w-4 h-4" />;
      case 'tiktok': return <Music className="w-4 h-4" />;
      default: return <Search className="w-4 h-4" />;
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const ReportSection = ({ title, content, sectionKey, isList = false }) => (
    <div className="glass-panel border border-white/10 mb-4">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-all"
      >
        <h3 className="text-sm font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{title}</h3>
        {expandedSections[sectionKey] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      {expandedSections[sectionKey] && (
        <div className="px-4 pb-4 text-sm text-zinc-300 leading-relaxed border-t border-white/10 pt-4">
          {isList && Array.isArray(content) ? (
            <ul className="space-y-2">
              {content.map((item, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <span className="text-[#00FF9D] mt-1">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>{content || 'Analysis in progress...'}</p>
          )}
        </div>
      )}
    </div>
  );

  const totalSelected = selectedArticles.length + selectedOsint.length;

  return (
    <div className="h-full overflow-hidden flex flex-col" data-testid="report-generator">
      {/* Header */}
      <div className="glass-panel border-b border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Barlow Condensed' }}>
              <FileText className="w-8 h-8 text-[#00FF9D]" />
              INTELLIGENCE REPORT GENERATOR
            </h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              Select multiple sources to generate comprehensive AI-powered intelligence reports
            </p>
          </div>
          <div className="flex items-center gap-3">
            {totalSelected > 0 && (
              <div className="flex items-center gap-2 px-4 py-2 bg-[#00FF9D]/10 border border-[#00FF9D]/30">
                <Check className="w-4 h-4 text-[#00FF9D]" />
                <span className="text-xs font-mono text-[#00FF9D]">{totalSelected} SOURCES SELECTED</span>
              </div>
            )}
            {totalSelected > 0 && (
              <button
                onClick={clearSelections}
                className="flex items-center gap-2 px-4 py-2 btn-secondary text-xs"
              >
                <X className="w-4 h-4" />
                CLEAR
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {[
            { id: 'sources', label: 'Select Sources' },
            { id: 'configure', label: 'Configure Report' },
            { id: 'report', label: 'View Report' },
            { id: 'history', label: 'Report History' }
          ].map(tab => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                activeTab === tab.id
                  ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50'
                  : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Sources Tab */}
        {activeTab === 'sources' && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {/* News Articles Section */}
            <div className="glass-panel tactical-border p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Newspaper className="w-5 h-5 text-[#00FF9D]" />
                  <h2 className="text-lg font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                    News Articles
                  </h2>
                </div>
                <button
                  onClick={loadArticles}
                  disabled={loadingArticles}
                  className="flex items-center gap-2 px-3 py-1.5 btn-secondary text-xs"
                >
                  <RefreshCw className={`w-3 h-3 ${loadingArticles ? 'animate-spin' : ''}`} />
                  REFRESH
                </button>
              </div>

              <div className="h-[400px] overflow-y-auto space-y-2 pr-2">
                {loadingArticles ? (
                  <div className="flex items-center justify-center h-full">
                    <RefreshCw className="w-6 h-6 text-[#00FF9D] animate-spin" />
                  </div>
                ) : articles.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-zinc-500">
                    <p className="text-sm font-mono">No articles available</p>
                  </div>
                ) : (
                  articles.map(article => {
                    const isSelected = selectedArticles.find(a => a.id === article.id);
                    return (
                      <div
                        key={article.id}
                        data-testid="article-selection-card"
                        onClick={() => toggleArticleSelection(article)}
                        className={`p-3 border cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-[#00FF9D]/10 border-[#00FF9D]/50'
                            : 'bg-black/20 border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isSelected ? 'border-[#00FF9D] bg-[#00FF9D]' : 'border-white/30'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-black" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-[9px] text-zinc-500 font-mono uppercase">{article.source}</span>
                              <span className={`text-[9px] px-1.5 py-0.5 font-bold uppercase ${
                                article.threat_level === 'Critical' ? 'bg-red-500/20 text-red-400' :
                                article.threat_level === 'High' ? 'bg-yellow-500/20 text-yellow-400' :
                                'bg-zinc-500/20 text-zinc-400'
                              }`}>
                                {article.threat_level}
                              </span>
                            </div>
                            <h3 className="text-xs font-bold text-white line-clamp-2">{article.title}</h3>
                            <p className="text-[10px] text-zinc-500 mt-1 line-clamp-2">{article.summary}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              <div className="mt-4 pt-3 border-t border-white/10 text-xs text-zinc-500 font-mono">
                {selectedArticles.length} articles selected
              </div>
            </div>

            {/* OSINT Section */}
            <div className="glass-panel tactical-border p-4">
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-5 h-5 text-[#00FF9D]" />
                <h2 className="text-lg font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                  OSINT Data
                </h2>
              </div>

              {/* OSINT Search */}
              <div className="mb-4 space-y-3">
                <input
                  type="text"
                  value={osintKeywords}
                  onChange={(e) => setOsintKeywords(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchOsint()}
                  className="w-full bg-black/50 border border-white/10 focus:border-[#00FF9D]/50 text-white px-3 py-2 text-sm font-mono outline-none"
                  placeholder="Search keywords..."
                  data-testid="osint-search-input"
                />
                <div className="flex gap-2">
                  {['Twitter', 'Telegram', 'Instagram', 'TikTok'].map(platform => (
                    <button
                      key={platform}
                      onClick={() => setOsintPlatforms(prev => 
                        prev.includes(platform) ? prev.filter(p => p !== platform) : [...prev, platform]
                      )}
                      className={`px-2 py-1 text-[10px] font-bold uppercase transition-all ${
                        osintPlatforms.includes(platform)
                          ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50'
                          : 'bg-white/5 text-zinc-500 border border-white/10'
                      }`}
                    >
                      {platform}
                    </button>
                  ))}
                </div>
                <button
                  onClick={searchOsint}
                  disabled={searchingOsint || !osintKeywords.trim()}
                  className="w-full btn-primary py-2 text-xs disabled:opacity-50"
                  data-testid="search-osint-btn"
                >
                  {searchingOsint ? 'SEARCHING...' : 'SEARCH OSINT'}
                </button>
              </div>

              <div className="h-[300px] overflow-y-auto space-y-2 pr-2">
                {osintResults.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-zinc-500">
                    <div className="text-center">
                      <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-xs font-mono">Search for OSINT data</p>
                    </div>
                  </div>
                ) : (
                  osintResults.map(osint => {
                    const isSelected = selectedOsint.find(o => o.id === osint.id);
                    return (
                      <div
                        key={osint.id}
                        data-testid="osint-selection-card"
                        onClick={() => toggleOsintSelection(osint)}
                        className={`p-3 border cursor-pointer transition-all ${
                          isSelected
                            ? 'bg-[#00FF9D]/10 border-[#00FF9D]/50'
                            : 'bg-black/20 border-white/10 hover:border-white/30'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-5 h-5 border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isSelected ? 'border-[#00FF9D] bg-[#00FF9D]' : 'border-white/30'
                          }`}>
                            {isSelected && <Check className="w-3 h-3 text-black" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {getPlatformIcon(osint.platform)}
                              <span className="text-[9px] text-zinc-400 font-mono">{osint.platform}</span>
                              <span className="text-[9px] text-zinc-500">{osint.author}</span>
                            </div>
                            <p className="text-xs text-white line-clamp-2">{osint.content}</p>
                            <div className="flex items-center gap-3 mt-1 text-[9px] text-zinc-600 font-mono">
                              <span>♥ {osint.likes}</span>
                              <span>↻ {osint.shares}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              
              <div className="mt-4 pt-3 border-t border-white/10 text-xs text-zinc-500 font-mono">
                {selectedOsint.length} OSINT items selected
              </div>
            </div>
          </div>
        )}

        {/* Configure Tab */}
        {activeTab === 'configure' && (
          <div className="max-w-3xl mx-auto">
            <div className="glass-panel tactical-border p-6">
              <div className="flex items-center gap-3 mb-6">
                <Sparkles className="w-6 h-6 text-[#00FF9D]" />
                <h2 className="text-xl font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>
                  Report Configuration
                </h2>
              </div>

              {/* Selection Summary */}
              <div className="mb-6 p-4 bg-black/20 border border-white/10">
                <h3 className="text-xs font-bold uppercase mb-3 text-zinc-400">Selected Sources</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 text-sm">
                      <Newspaper className="w-4 h-4 text-[#00FF9D]" />
                      <span className="font-mono">{selectedArticles.length} Articles</span>
                    </div>
                    {selectedArticles.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {selectedArticles.slice(0, 3).map(a => (
                          <li key={a.id} className="text-[10px] text-zinc-500 truncate">• {a.title}</li>
                        ))}
                        {selectedArticles.length > 3 && (
                          <li className="text-[10px] text-zinc-600">+ {selectedArticles.length - 3} more</li>
                        )}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2 text-sm">
                      <Search className="w-4 h-4 text-[#00FF9D]" />
                      <span className="font-mono">{selectedOsint.length} OSINT Items</span>
                    </div>
                    {selectedOsint.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {selectedOsint.slice(0, 3).map(o => (
                          <li key={o.id} className="text-[10px] text-zinc-500 truncate">• [{o.platform}] {o.content.slice(0, 40)}...</li>
                        ))}
                        {selectedOsint.length > 3 && (
                          <li className="text-[10px] text-zinc-600">+ {selectedOsint.length - 3} more</li>
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              {/* Report Title */}
              <div className="mb-6">
                <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">
                  Report Title *
                </label>
                <input
                  type="text"
                  value={reportTitle}
                  onChange={(e) => setReportTitle(e.target.value)}
                  className="w-full bg-black/50 border border-white/10 focus:border-[#00FF9D]/50 text-white px-4 py-3 text-sm font-mono outline-none"
                  placeholder="e.g., Middle East Security Assessment Q4 2026"
                  data-testid="report-title-input"
                />
              </div>

              {/* Focus Areas */}
              <div className="mb-6">
                <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">
                  Focus Areas
                </label>
                <div className="flex flex-wrap gap-2">
                  {['military', 'geopolitical', 'economic', 'cyber', 'terrorism', 'regional stability'].map(area => (
                    <button
                      key={area}
                      onClick={() => toggleFocusArea(area)}
                      className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-all ${
                        focusAreas.includes(area)
                          ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50'
                          : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {area}
                    </button>
                  ))}
                </div>
              </div>

              {/* Classification */}
              <div className="mb-6">
                <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">
                  Classification
                </label>
                <div className="flex gap-2">
                  {['UNCLASSIFIED', 'CONFIDENTIAL', 'SECRET'].map(level => (
                    <button
                      key={level}
                      onClick={() => setClassification(level)}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                        classification === level
                          ? level === 'SECRET' ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                          : level === 'CONFIDENTIAL' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                          : 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50'
                          : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                      }`}
                    >
                      {level}
                    </button>
                  ))}
                </div>
              </div>

              {/* Report Language */}
              <div className="mb-8">
                <label className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono mb-2 block">
                  <Globe className="w-3 h-3 inline mr-1" />
                  Report Language
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setReportLanguage('en')}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                      reportLanguage === 'en'
                        ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50'
                        : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    🇬🇧 English
                  </button>
                  <button
                    onClick={() => setReportLanguage('ar')}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-all ${
                      reportLanguage === 'ar'
                        ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50'
                        : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    🇸🇦 العربية
                  </button>
                </div>
              </div>

              {/* Generate Button */}
              <button
                onClick={generateReport}
                disabled={generating || totalSelected === 0 || !reportTitle.trim()}
                className="w-full btn-primary py-4 text-sm flex items-center justify-center gap-3 disabled:opacity-50"
                data-testid="generate-report-btn"
              >
                {generating ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>GENERATING INTELLIGENCE REPORT...</span>
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5" />
                    <span>GENERATE AI INTELLIGENCE REPORT</span>
                  </>
                )}
              </button>

              {totalSelected === 0 && (
                <p className="mt-3 text-center text-xs text-zinc-500 font-mono">
                  Please select at least one source from the Sources tab
                </p>
              )}
            </div>
          </div>
        )}

        {/* Report Tab */}
        {activeTab === 'report' && (
          <div className="max-w-4xl mx-auto">
            {report ? (
              <div data-testid="generated-report">
                {/* Report Header */}
                <div className="glass-panel tactical-border p-6 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className={`inline-block px-3 py-1 text-xs font-bold uppercase mb-2 ${
                        report.classification === 'SECRET' ? 'bg-red-500/20 text-red-400' :
                        report.classification === 'CONFIDENTIAL' ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-[#00FF9D]/20 text-[#00FF9D]'
                      }`}>
                        {report.classification}
                      </div>
                      <h1 className="text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                        {report.report_title}
                      </h1>
                      <p className="text-xs text-zinc-400 font-mono mt-2">
                        Generated: {new Date(report.generated_at).toLocaleString()} | 
                        Sources: {report.sources_analyzed} | 
                        DefenseWatch Platform
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => exportToPptx('en')}
                        disabled={exporting}
                        className="flex items-center gap-2 px-4 py-3 btn-primary disabled:opacity-50"
                        data-testid="export-pptx-en-btn"
                      >
                        {exporting ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span>EXPORT (EN)</span>
                      </button>
                      <button
                        onClick={() => exportToPptx('ar')}
                        disabled={exporting}
                        className="flex items-center gap-2 px-4 py-3 bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50 hover:bg-[#00FF9D]/20 transition-all disabled:opacity-50"
                        data-testid="export-pptx-ar-btn"
                      >
                        {exporting ? (
                          <Loader className="w-4 h-4 animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span>EXPORT (AR)</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Report Sections */}
                <ReportSection 
                  title="Executive Summary" 
                  content={report.executive_summary} 
                  sectionKey="executive" 
                />
                <ReportSection 
                  title="Key Findings" 
                  content={report.key_findings} 
                  sectionKey="findings" 
                  isList={true}
                />
                <ReportSection 
                  title="Threat Assessment" 
                  content={report.threat_assessment} 
                  sectionKey="threat" 
                />
                <ReportSection 
                  title="Regional Analysis" 
                  content={report.regional_analysis} 
                  sectionKey="regional" 
                />
                <ReportSection 
                  title="Strategic Implications" 
                  content={report.strategic_implications} 
                  sectionKey="strategic" 
                />
                <ReportSection 
                  title="Source Correlation" 
                  content={report.source_correlation} 
                  sectionKey="correlation" 
                />
                <ReportSection 
                  title="Recommendations" 
                  content={report.recommendations} 
                  sectionKey="recommendations" 
                  isList={true}
                />
              </div>
            ) : (
              <div className="glass-panel tactical-border p-12 text-center">
                <FileText className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  NO REPORT GENERATED
                </h2>
                <p className="text-sm text-zinc-400 mb-6">
                  Select sources and configure your report to generate an AI-powered intelligence analysis
                </p>
                <button
                  onClick={() => setActiveTab('sources')}
                  className="btn-primary px-6 py-2"
                >
                  SELECT SOURCES
                </button>
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="max-w-4xl mx-auto">
            <div className="glass-panel tactical-border p-4">
              <h2 className="text-lg font-bold uppercase mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                Report History
              </h2>
              
              {savedReports.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-sm text-zinc-400">No reports generated yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {savedReports.map(savedReport => (
                    <div
                      key={savedReport.id}
                      className="p-4 bg-black/20 border border-white/10 hover:border-[#00FF9D]/30 cursor-pointer transition-all"
                      onClick={() => loadSavedReport(savedReport)}
                      data-testid="saved-report-card"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase mb-1 ${
                            savedReport.classification === 'SECRET' ? 'bg-red-500/20 text-red-400' :
                            savedReport.classification === 'CONFIDENTIAL' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-[#00FF9D]/20 text-[#00FF9D]'
                          }`}>
                            {savedReport.classification}
                          </div>
                          <h3 className="text-sm font-bold text-white">{savedReport.report_title}</h3>
                          <p className="text-[10px] text-zinc-500 font-mono mt-1">
                            {new Date(savedReport.generated_at).toLocaleString()} | {savedReport.sources_analyzed} sources
                          </p>
                        </div>
                        <ChevronDown className="w-5 h-5 text-zinc-500" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
